import React, { useEffect, useRef, useState } from 'react'
import { World } from '../three/World.js'

const STATUS_COLOR = { idle: '#69f0ae', building: '#7df9ff', critiquing: '#ffd54f', optimizing: '#b388ff', 'responding-to-human': '#ff8a65' }

export default function Panel({ agent, solo, onSolo, throttle }) {
  const wrapRef = useRef(null)
  const worldRef = useRef(null)
  const [driving, setDriving] = useState(false)
  const [hideModal, setHideModal] = useState(false)

  useEffect(() => {
    const w = new World(wrapRef.current, agent.window - 1)
    worldRef.current = w
    let raf
    const loop = () => { w.render(); raf = requestAnimationFrame(loop) }
    raf = requestAnimationFrame(loop)
    return () => { cancelAnimationFrame(raf); w.dispose() }
  }, [])

  // Apply the latest worldState every render — guarantees the 3D scene tracks
  // state changes from ticks AND prompts (no missed updates from ref tricks).
  useEffect(() => { worldRef.current?.setState(agent.worldState) })
  useEffect(() => { worldRef.current?.setThrottle(throttle && !solo) }, [throttle, solo])
  useEffect(() => { setTimeout(() => worldRef.current?.resize(), 60) }, [solo])

  // Navigation: click to drive (FPV, no avatar). Esc / click-out releases.
  useEffect(() => {
    if (!driving) return
    const w = worldRef.current
    w.focus(true)
    const kd = e => { if (e.key === 'Escape') setDriving(false); else w.onKey(e, true) }
    const ku = e => w.onKey(e, false)
    const mm = e => w.onMouseMove(e.movementX || 0)
    window.addEventListener('keydown', kd)
    window.addEventListener('keyup', ku)
    window.addEventListener('mousemove', mm)
    return () => { w.focus(false); window.removeEventListener('keydown', kd); window.removeEventListener('keyup', ku); window.removeEventListener('mousemove', mm) }
  }, [driving])

  const ws = agent.worldState || {}
  return (
    <div className={`panel${solo ? ' solo' : ''}${driving ? ' driving' : ''}`}
         onClick={() => !driving && setDriving(true)}>
      <div className="canvasWrap" ref={wrapRef} />
      <div className="label">{agent.name}</div>

      <button className="btn icon" style={{ position: 'absolute', left: 8, top: 8, zIndex: 7 }}
              onClick={(e) => { e.stopPropagation(); onSolo() }} title="solo / restore">{solo ? '▣' : '◱'}</button>

      {hideModal ? <div className="cornerDot" style={{ background: STATUS_COLOR[agent.status] || '#69f0ae' }} />
        : (
        <div className="agentModal glass">
          <div className="am-head">
            <span className="dot" style={{ background: STATUS_COLOR[agent.status] || '#69f0ae', boxShadow: `0 0 8px ${STATUS_COLOR[agent.status] || '#69f0ae'}` }} />
            <b>{agent.name}</b>
            <span className="status">{driving ? 'navigated-by-user' : agent.status}</span>
            <span style={{ flex: 1 }} />
            <button className="btn icon" style={{ padding: '2px 6px', fontSize: 11 }}
                    onClick={(e) => { e.stopPropagation(); setHideModal(true) }} title="hide">▾</button>
          </div>
          <div className="thesis">{agent.thesis}</div>
          <div className="row"><span>mood</span><b>{ws.mood}</b></div>
          <div className="row"><span>variable</span><b>{agent.variables?.[0]?.clean?.slice(0, 22) || '—'}</b></div>
          <div className="row"><span>improve</span><b>{Math.round((agent.improvementScore || 0) * 100)}%</b></div>
          <div className="row"><span>divergence</span><b>{(agent.divergence || 0).toFixed(2)}</b></div>
          <div className="row"><span>engine</span><b style={{ fontSize: 10 }}>{agent.model || '—'}</b></div>
          {agent.lastCritiqueReceived && (
            <div className="crit">◂ from W{agent.lastCritiqueReceived.from}: {agent.lastCritiqueReceived.claim?.slice(0, 80)}</div>
          )}
        </div>
      )}
      {hideModal && <button className="btn icon modalHideBtn glass" style={{ display: 'flex' }}
                            onClick={(e) => { e.stopPropagation(); setHideModal(false) }} title="show agent">◓</button>}

      <div className="focusHint">{driving ? 'WASD / arrows to walk · Esc to release' : 'click to walk this world'}</div>
    </div>
  )
}
